import { FlightSearch, SkyscannerResultsV3 } from 'types';
import { format } from 'date-fns';

const priceRelation: Record<SkyscannerResultsV3.PriceUnit, number> = {
  PRICE_UNIT_WHOLE: 1,
  PRICE_UNIT_CENTI: 100,
  PRICE_UNIT_MILLI: 1000,
  PRICE_UNIT_MICRO: 1000000,
  PRICE_UNIT_UNSPECIFIED: 1,
};

export const mapResults = (poll: SkyscannerResultsV3.LivePricingResponse)
  : FlightSearch.LiveResults => {
  return {
    sessionId: poll.sessionToken,
    itineraries: poll.content.sortingOptions.best.map((it) => {
      const itinerary = poll.content.results.itineraries[it.itineraryId];
      const prices = ([] as FlightSearch.Price[])
        .concat(...itinerary.pricingOptions.map(it => mapPrice(it, poll.content.results)))
        .distinct(it => it.id);

      const legs = itinerary.legIds.map(id => mapLeg(poll.content.results, id));
      return {
        prices,
        outboundLeg: legs[0],
        inboundLeg: legs.length > 1 ? legs[1] : null,
        dealsCount: prices.length,
        adults: 1,
        duplicate: null,
      }
    }),
    state: poll.status,
    action: poll.action,
  }
}

export const mapAgent = (id: string, results: SkyscannerResultsV3.Results): FlightSearch.Agent => {
  const agent = results.agents[id];

  const matchCarrier = () => {
    const carrierRaw = Object
      .keys(results.carriers)
      .map(key => { 
        return {
          'id': key,
          'carrier': results.carriers[key],
        }
      })
      .find(it => it.carrier.name.toLowerCase().includes(agent.name.toLowerCase()));

    if (!carrierRaw) {
      return null;
    }

    return mapCarrier(results, carrierRaw.id);
  }

  return {
    id,
    name: agent.name,
    type: agent.type,
    imageUrl: agent.imageUrl,
    carrier: matchCarrier(),
  }
}

export const mapPrice = (price: SkyscannerResultsV3.PricingOption, results: SkyscannerResultsV3.Results): FlightSearch.Price => {
  const items: FlightSearch.PriceItems[] = price.items.map(x => {
    return {
      price: parsePrice(x.price.amount, x.price.unit),
      link: x.deepLink,
      agent: mapAgent(x.agentId, results),
      fares: x.fares
    }
  });

  const totalPrice = parseFloat(items.sum(it => it.price).toFixed(2));
  const isMashUp = items.every(it => it.agent.type === SkyscannerResultsV3.AgentType.Airline) 
    && items.length > 1;

  return {
    id: price.id,
    items,
    totalPrice,
    isMashUp,
  };
}

export const parsePrice = (priceString: string, unit: SkyscannerResultsV3.PriceUnit): number => {
  try {
    const price: number = +priceString;
    return price / priceRelation[unit];
  } catch (e) {
    return 0;
  }
}

export const mapLeg = (result: SkyscannerResultsV3.Results, legId: string): FlightSearch.Leg => {
  const dryLeg = result.legs[legId];
  const segments = dryLeg.segmentIds.map(it => mapSegment(result, it));
  const duration = mapDuration(dryLeg.durationInMinutes);
  const carriers = [...dryLeg.marketingCarrierIds, ...dryLeg.operatingCarrierIds]
    .distinct()
    .map(it => mapCarrier(result, it));
  return {
    id: legId,
    dates: mapDates(dryLeg),
    carriers,
    duration,
    segments,
    stations: mapStation(segments),
    stopsDuration: mapStopDuration(duration.duration, segments),
  }
}

export const mapDates = (leg: SkyscannerResultsV3.Leg): FlightSearch.Dates => {
  const arrivalDate = buildDate(leg.arrivalDateTime);
  const departureDate = buildDate(leg.departureDateTime);

  const departureDisplay = format(departureDate, 'HH:mm');
  const arrivalDisplay = format(arrivalDate, 'HH:mm');

  return {
    arrivalDate,
    departureDate,
    arrivalDisplay,
    departureDisplay
  }
}

export const mapStation = (segments: FlightSearch.Segment[]): FlightSearch.Stations => {
  const stops: FlightSearch.Place[] = [];
  const origin = segments[0].originStation;
  const destination = segments[segments.length - 1].destinanionStation;
  segments.forEach((it, i) => {
    if (stops.findIndex(x => x.id === it.originStation.id) < 0
      && it.originStation.id != origin.id && it.originStation.id != destination.id) {
      stops.push(it.originStation);
    }
    if (stops.findIndex(x => x.id === it.destinanionStation.id) < 0
      && it.destinanionStation.id != origin.id && it.destinanionStation.id != destination.id) {
      stops.push(it.destinanionStation);
    }
  });

  return {
    origin,
    destination,
    stops,
  };
}

export const mapCarrier = (result: SkyscannerResultsV3.Results, id: string): FlightSearch.Carrier => {
  try {
    const carrier = result.carriers[id];
    const alliance = mapAlliance(result, carrier.allianceId);
    return {
      id,
      alliance,
      displayCode: carrier.iata,
      name: carrier.name,
      imageUrl: carrier.imageUrl,
    };
  } catch (e) {
    return null;
  }

}

export const mapAlliance = (result: SkyscannerResultsV3.Results, id?: string): FlightSearch.Alliance => {
  try {
    const alliance = result.alliances[id];
    return {
      id,
      name: alliance.name
    }
  } catch (e) {
    return null;
  }
}

export const mapDuration = (duration: number): FlightSearch.Duration => {
  const convertMinsToHrsMins = (mins) => {
    let h = Math.floor(mins / 60);
    let m = Math.round(mins % 60);
    return `${h > 0 ? h + 'h' : ''}${h > 0 && m > 0 ? ' ' : ''}${m > 0 ? m + 'm' : ''}`;
  }
  return {
    duration,
    title: convertMinsToHrsMins(duration),
  }
}

export const mapStopDuration = (duration: number, segments: FlightSearch.Segment[]): FlightSearch.Duration => {
  return mapDuration(duration - segments.reduce((sum, it) => sum + it.duration.duration, 0) || 0);
}

export const mapSegment = (poll: SkyscannerResultsV3.Results, id: string): FlightSearch.Segment => {

  try {
    const segment = poll.segments[id];
    return {
      id,
      marketingCarrier: mapCarrier(poll, segment.marketingCarrierIds),
      operatingCarrier: mapCarrier(poll, segment.operatingCarrierIds),
      duration: mapDuration(segment.durationInMinutes),
      originStation: mapPlaces(poll, segment.originPlaceId),
      destinanionStation: mapPlaces(poll, segment.destinationPlaceId),
      departureDate: buildDate(segment.departureDateTime),
      arrivalDate: buildDate(segment.arrivalDateTime),
    }
  } catch (e) {
    return undefined;
  }

}

export const buildDate = (date: SkyscannerResultsV3.DateType): Date => {
  const tmp = new Date();
  tmp.setFullYear(date.year);
  tmp.setMonth(date.month - 1);
  tmp.setDate(date.day);
  tmp.setHours(date.hour);
  tmp.setMinutes(date.minute);
  tmp.setSeconds(date.second);
  return tmp;
}

export const mapPlaces = (results: SkyscannerResultsV3.Results, id: string): FlightSearch.Place => {
  const place = results.places[id];
  return {
    id,
    code: place.iata,
    type: place.type,
    name: place.name,
    parent: place.parentId ? mapPlaces(results, place.parentId) : null,
  }
}